/**
 * MPC (Multi-Party Computation) Wallet State Atoms
 *
 * This module provides Jotai atoms for managing MPC wallet state,
 * specifically for Namada privacy-preserving operations.
 *
 * @module stores/mpc/atoms
 */

import { formatCombinedNamadaAddress } from '@polaris/core/namada'
import { featureFlagsAtom } from '@polaris/core/stores'
import { vaultSessionDataAtom } from '@polaris/core/stores/mpc/atom'
import { mpcWalletAddressesAtom } from '@polaris/core/stores/mpc/queries'
import { atom } from 'jotai'
import { atomWithQuery } from 'jotai-tanstack-query'
import { getMpcNamadaKeysQuery } from '@/src/queries/keys/mpc'

/**
 * Atom that derives Namada cryptographic keys from MPC session data.
 *
 * This atom fetches Namada-specific keys (viewing key, spending key, payment address)
 * using the vault session signatures. These keys are derived deterministically
 * from the MPC root key and are required for privacy-preserving transactions.
 *
 * @returns Query result containing:
 * - `viewingKey` - Used to decrypt and view shielded transaction history
 * - `spendingKey` - Used to authorize shielded transfers (kept secure in MPC)
 * - `paymentAddress` - Shielded address for receiving private payments
 * - `pseudoExtendedKey` - Combined key format needed for privacy route execution
 *
 * @remarks
 * - Only enabled when the `privacy` feature flag is active
 * - Requires valid vault session data (authSig + sessionKeyPair)
 * - Returns disabled query if session data is missing
 *
 * @example
 * ```tsx
 * const { data: namadaKeys, isLoading } = useAtomValue(mpcNamadaKeysAtom)
 * if (namadaKeys) {
 *   console.log('Payment address:', namadaKeys.paymentAddress)
 * }
 * ```
 */
export const mpcNamadaKeysAtom = atomWithQuery((get) => {
  const vaultSessionData = get(vaultSessionDataAtom)
  const { privacy } = get(featureFlagsAtom)

  const sessionKeyData =
    vaultSessionData?.authSig && vaultSessionData?.sessionKeyPair
      ? {
          walletSig: vaultSessionData.authSig,
          sessionKeyPair: vaultSessionData.sessionKeyPair,
        }
      : undefined

  return getMpcNamadaKeysQuery(sessionKeyData, privacy)
})

/**
 * Derived atom that provides MPC wallet addresses with Namada address override.
 *
 * This atom extends the base `mpcWalletAddressesAtom` by replacing the
 * Namada address with a combined format that includes both the viewing key
 * and shielded payment address:
 *
 * ```
 * "vk:<viewing_key>;pay:<shielded_address>"
 * ```
 *
 * @returns Query result containing wallet addresses for all supported chains,
 * with Namada address in combined format if privacy keys are available
 *
 * @remarks
 * This override is necessary because Namada vault signing is handled client-side
 * (unlike other chains which use server-side runners). The combined format allows
 * the client to:
 * 1. Use the viewing key to decrypt transaction history
 * 2. Use the payment address for receiving shielded funds
 *
 * @example
 * ```tsx
 * const { data: addresses } = useAtomValue(mpcWalletAddressesWithNamadaOverrideAtom)
 * // addresses.namada = "vk:zvknam1...;pay:znam1..."
 * // addresses.ethereum = "0x..."
 * // addresses.cosmos = "cosmos1..."
 * ```
 *
 * @see {@link mpcWalletAddressesAtom} - Base atom without Namada override
 * @see {@link mpcNamadaKeysAtom} - Source of Namada keys for override
 */
export const mpcWalletAddressesWithNamadaOverrideAtom = atom((get) => {
  const result = get(mpcWalletAddressesAtom)
  const namadaKeys = get(mpcNamadaKeysAtom)

  if (!result.data || !namadaKeys.data) {
    return result
  }

  const combinedAddress = formatCombinedNamadaAddress(namadaKeys.data)

  return {
    ...result,
    data: combinedAddress
      ? { ...result.data, namada: combinedAddress }
      : result.data,
  }
})
